import type { Breakpoint, TypographyVariantsOptions } from '@mui/material/styles';

import { pxToRem, setFont } from 'minimal-shared/utils';

import { createTheme } from '@mui/material/styles';

import { themeConfig } from '../theme-config';

// ----------------------------------------------------------------------

/**
 * TypeScript extension for MUI theme augmentation.
 * @to {@link file://./../extend-theme-types.d.ts}
 */

export type TypographyVariantsExtend = {
  fontWeightSemiBold: React.CSSProperties['fontWeight'];
  fontWeightExtraBold: React.CSSProperties['fontWeight'];
  fontSecondaryFamily: React.CSSProperties['fontFamily'];
};

/**
 * Generates responsive font styles for given breakpoints
 * @param sizes - Object mapping breakpoints to font sizes in pixels
 * @returns CSS media query styles for responsive font sizes
 */
type FontSizesInput = Partial<Record<Breakpoint, number>>;
type FontSizesResult = Record<string, { fontSize: React.CSSProperties['fontSize'] }>;

function responsiveFontSizes(sizes: FontSizesInput): FontSizesResult {
  const {
    breakpoints: { keys, up },
  } = createTheme();

  return keys.reduce((styles, breakpoint) => {
    const size = sizes[breakpoint];

    if (size !== undefined && size >= 0) {
      styles[up(breakpoint)] = {
        fontSize: pxToRem(size),
      };
    }

    return styles;
  }, {} as FontSizesResult);
}

// ----------------------------------------------------------------------

const primaryFont = setFont(themeConfig.fontFamily.primary);
const secondaryFont = setFont(themeConfig.fontFamily.secondary);

const baseTypography: TypographyVariantsOptions = {
  fontFamily: primaryFont,
  fontSecondaryFamily: secondaryFont,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  fontWeightExtraBold: 800,
};

/* **********************************************************************
 * 📦 Final
 * **********************************************************************/
/**
 * Line height is set as a unitless ratio: 22 / 14 ≈ 1.57
 * - 22px is the desired visual line height
 * - 14px is the font size
 * This keeps the line height scalable and responsive.
 */
export const typography: TypographyVariantsOptions = {
  ...baseTypography,
  // h1–h5 use DM Serif Display at regular weight — serifs look best at 400
  h1: {
    fontFamily: secondaryFont,
    fontWeight: baseTypography.fontWeightRegular,
    lineHeight: 1.05,
    letterSpacing: '-0.03em',
    fontSize: pxToRem(44),
    ...responsiveFontSizes({ sm: 48, md: 52, lg: 56 }),
  },
  h2: {
    fontFamily: secondaryFont,
    fontWeight: baseTypography.fontWeightRegular,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    fontSize: pxToRem(32),
    ...responsiveFontSizes({ sm: 36, md: 40, lg: 44 }),
  },
  h3: {
    fontFamily: secondaryFont,
    fontWeight: baseTypography.fontWeightRegular,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
    fontSize: pxToRem(24),
    ...responsiveFontSizes({ sm: 26, md: 28, lg: 32 }),
  },
  h4: {
    fontFamily: secondaryFont,
    fontWeight: baseTypography.fontWeightRegular,
    lineHeight: 1.25,
    fontSize: pxToRem(20),
    ...responsiveFontSizes({ md: 24 }),
  },
  h5: {
    fontFamily: secondaryFont,
    fontWeight: baseTypography.fontWeightRegular,
    lineHeight: 1.3,
    fontSize: pxToRem(18),
    ...responsiveFontSizes({ sm: 19 }),
  },
  // h6 stays DM Sans — it's used for UI labels, not display text
  h6: {
    fontWeight: baseTypography.fontWeightSemiBold,
    lineHeight: 1.4,
    fontSize: pxToRem(15),
    ...responsiveFontSizes({ sm: 16 }),
  },
  subtitle1: {
    fontWeight: baseTypography.fontWeightSemiBold,
    lineHeight: 1.5,
    fontSize: pxToRem(15),
  },
  subtitle2: {
    fontWeight: baseTypography.fontWeightSemiBold,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  body1: {
    lineHeight: 1.65,
    fontSize: pxToRem(15),
  },
  body2: {
    lineHeight: 1.55,
    fontSize: pxToRem(13),
  },
  caption: {
    lineHeight: 1.5,
    fontSize: pxToRem(12),
  },
  overline: {
    fontWeight: baseTypography.fontWeightBold,
    lineHeight: 1.5,
    fontSize: pxToRem(11),
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },
  button: {
    fontWeight: baseTypography.fontWeightSemiBold,
    lineHeight: 24 / 14,
    fontSize: pxToRem(13),
    letterSpacing: '0.01em',
    textTransform: 'unset',
  },
};
